"use client";

import React from "react";
import SubPageLayout from "@/components/common/SubPageLayout";
import DiagramSection from "@/components/diagrams/DiagramSection";
import DiagramCard from "@/components/diagrams/DiagramCard";
import PlainWeaveDiagram from "@/components/diagrams/PlainWeaveDiagram";
import TwillWeaveDiagram from "@/components/diagrams/TwillWeaveDiagram";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
import { SQUARE_MESH_CONTENT } from "./content";
export default function SquareMeshPage() {
  const {
    lang
  } = useLanguage();
  const content = pick(lang, SQUARE_MESH_CONTENT);
  const diagramTitle = pick(lang, {
    en: "Square Weave Architectures",
    zh: "方孔编织网结构对比",
    es: "Arquitecturas de tejido de malla cuadrada",
    de: "Webarten des Quadratmaschengewebes",
    fr: "Architectures de tissage de toiles carrées",
    pt: "Arquiteturas de tecedura de malha quadrada"
  });
  const diagramLead = pick(lang, {
    en: "Comparison of the two orthogonal wire weaving constructions standardized under ISO 9044 and ASTM E2016.",
    zh: "ISO 9044 与 ASTM E2016 标准下两类正交金属丝织造结构的几何特征对比。",
    es: "Comparación de las dos construcciones ortogonales estandarizadas según ISO 9044 y ASTM E2016.",
    de: "Vergleich der beiden rechtwinkligen Webkonstruktionen nach ISO 9044 und ASTM E2016.",
    fr: "Comparaison des deux structures orthogonales normalisées selon ISO 9044 et ASTM E2016.",
    pt: "Comparação das duas construções ortogonais padronizadas sob as normas ISO 9044 e ASTM E2016."
  });
  const plainTitle = pick(lang, {
    en: "1:1 Plain Weave",
    zh: "1:1 平纹编织",
    es: "Ligamento liso 1:1",
    de: "1:1 Leinwandbindung",
    fr: "Tissage uni 1:1",
    pt: "Ponto liso 1:1"
  });
  const plainDesc = pick(lang, {
    en: "Each warp wire crosses alternately over and under each weft wire. Produces square apertures with predictable flow across 1 to 635 mesh.",
    zh: "经丝与纬丝按一上一下逐根交替穿梭，在 1 至 635 目范围内形成均匀正方孔与稳定的流道。",
    es: "Cada hilo de urdimbre cruza alternadamente uno arriba y uno abajo. Aberturas cuadradas regulares de 1 a 635 mesh.",
    de: "Jeder Kettdraht kreuzt abwechselnd über und unter jeden Schussdraht. Gleichmäßige Quadratmaschen von 1 bis 635 Mesh.",
    fr: "Chaque fil de chaîne passe alternativement dessus et dessous chaque fil de trame. Ouvertures carrées régulières de 1 à 635 mesh.",
    pt: "Cada fio de urdume cruza alternadamente por cima e por baixo de cada fio de trama. Aberturas quadradas de 1 a 635 mesh."
  });
  const twillTitle = pick(lang, {
    en: "2:2 Twilled Weave",
    zh: "2:2 斜纹编织",
    es: "Ligamento de sarga 2:2",
    de: "2:2 Köperbindung",
    fr: "Tissage sergé 2:2",
    pt: "Ponto sarjado 2:2"
  });
  const twillDesc = pick(lang, {
    en: "Each warp wire passes over two and under two weft wires. Allows thicker wire diameters in fine counts from 100 to 635 mesh.",
    zh: "经丝按二上二下跨越两根纬丝交织，形成对角纹理，允许在 100 至 635 目细目数下使用更粗金属丝。",
    es: "Cada hilo de urdimbre pasa dos arriba y dos abajo. Permite alambres más gruesos en mallas finas de 100 a 635 mesh.",
    de: "Jeder Kettdraht verläuft über zwei und unter zwei Schussdrähte. Ermöglicht stärkere Drähte von 100 bis 635 Mesh.",
    fr: "Chaque fil de chaîne passe sur deux puis sous deux fils de trame. Permet des fils plus épais de 100 à 635 mesh.",
    pt: "Cada fio de urdume passa por cima de dois e por baixo de dois fios de trama. Permite arames mais grossos de 100 a 635 mesh."
  });
  const diagramSlot = <DiagramSection id="weave-diagrams" title={diagramTitle} lead={diagramLead} tone="muted">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <DiagramCard index="01" badge="1:1" title={plainTitle} description={plainDesc}>
          <PlainWeaveDiagram />
        </DiagramCard>
        <DiagramCard index="02" badge="2:2" title={twillTitle} description={twillDesc}>
          <TwillWeaveDiagram />
        </DiagramCard>
      </div>
    </DiagramSection>;
  return <SubPageLayout content={content} diagram={diagramSlot} />;
}